/**
 * Answers the desktop shell's "a break is starting, what should I suggest?"
 *
 * The shell runs the Pomodoro timer but has no Platform session; this app
 * has the session and the Apollo client. So on `breakStarting` this hook
 * fetches the learner's recordings, picks one that fits the break
 * (`selectBreakSession`), and replies. It always replies, with null when
 * there is nothing to offer, so the shell announces the break at once rather
 * than waiting out its timeout.
 *
 * Inert in a browser and in a shell too old to have the timer.
 */

import { gql, useApolloClient } from "@apollo/client";
import { useEffect } from "react";

import appInfo from "utility/app-info";
import { logError } from "utility/Function";

import type { WoneDesktopBridge } from "../bridge";
import { getPomodoroBridge, type BreakStartingMessage } from "./bridge";
import {
  loadServedIds,
  recordServed,
  selectBreakSession,
  type CandidateRecording,
} from "./selectBreakSession";

/**
 * Per list. Recordings are newest first, and fifty is several pages of
 * Discover.
 */
export const BREAK_CANDIDATE_LIMIT = 50;

/**
 * The newest recordings, plus the highlighted ones Discover shows as Rapid
 * Resets. The newest are mostly 20 to 45 minute classes, so on their own they
 * rarely fit a short break; the highlighted ones are the 2 to 5 minute
 * sessions, and they drop out of the newest fifty as classes are recorded.
 * Both lists hold the same recordings, so one id means one recording.
 */
export const BREAK_CANDIDATES = gql`
  fragment DesktopPomodoroBreakCandidate on LearnerRecording {
    id
    title
    videoDurationText
    sessionDuration
  }

  query DesktopPomodoroBreakCandidates($appInfo: AppInfo!, $first: Int) {
    recent: learnerContentList(
      id: "LearnerRecordings"
      filters: {}
      searchQuery: ""
      appInfo: $appInfo
      first: $first
    ) {
      __typename
      ... on LearnerRecordingsList {
        id
        items {
          __typename
          ...DesktopPomodoroBreakCandidate
        }
      }
    }
    highlighted: learnerContentList(
      id: "LearnerRecordingsHighlighted"
      filters: {}
      searchQuery: ""
      appInfo: $appInfo
      first: $first
    ) {
      __typename
      ... on LearnerRecordingsList {
        id
        items {
          __typename
          ...DesktopPomodoroBreakCandidate
        }
      }
    }
  }
`;

interface CandidateList {
  __typename?: string;
  items?: (CandidateRecording | null)[] | null;
}

interface QueryData {
  recent?: CandidateList | null;
  highlighted?: CandidateList | null;
}

function mergeCandidates(data: QueryData | undefined): CandidateRecording[] {
  const byId = new Map<string, CandidateRecording>();
  [data?.highlighted, data?.recent].forEach((list) => {
    (list?.items ?? []).forEach((item) => {
      if (item === null || typeof item?.id !== "string") return;
      if (!byId.has(item.id)) byId.set(item.id, item);
    });
  });
  return Array.from(byId.values());
}

export function usePomodoroBreakSession(
  bridge: WoneDesktopBridge | null,
  authenticated: boolean
): void {
  const client = useApolloClient();
  const pomodoro = getPomodoroBridge(bridge);

  useEffect(() => {
    if (pomodoro === null) return undefined;

    const answer = async (message: BreakStartingMessage): Promise<void> => {
      if (!authenticated) {
        await pomodoro.replyBreakSession({
          requestId: message.requestId,
          session: null,
        });
        return;
      }
      let candidates: CandidateRecording[] = [];
      try {
        const { data } = await client.query<QueryData>({
          query: BREAK_CANDIDATES,
          variables: { appInfo, first: BREAK_CANDIDATE_LIMIT },
          // Cached lists are fine: recordings change daily, breaks every
          // half hour, and a cache hit answers well inside the shell's wait.
          fetchPolicy: "cache-first",
        });
        candidates = mergeCandidates(data);
      } catch (error) {
        // The break is still announced without a suggestion; the failure is
        // reported rather than swallowed.
        logError("pomodoro break candidates failed", error);
      }
      const session = selectBreakSession(
        candidates,
        message.breakMs,
        loadServedIds()
      );
      await pomodoro.replyBreakSession({
        requestId: message.requestId,
        session,
      });
      // Recorded only once the shell has taken the reply: a rejected or
      // failed one never reached the learner, so it should not use up the
      // suggestion.
      if (session !== null) recordServed(session.id);
    };

    return pomodoro.onBreakStarting((message) => {
      answer(message).catch((error) =>
        logError("pomodoro break reply failed", error)
      );
    });
  }, [pomodoro, authenticated, client]);
}
